/**
 * ============================================================================
 * Iron Meridian - Turn Summary Modal
 * ============================================================================
 * 
 * Modal overlay that appears after processTurn() completes.
 * Shows combat results, economy changes, and AI intent summaries.
 * Premium glass-morphism design with sections for each aspect of the turn.
 */

import { useEffect, useCallback, useRef } from 'react'

// ============================================================================
// SUB-COMPONENTS
// ============================================================================

function CombatCard({ combat, countries }) {
  const attackerName = countries[combat.attacker]?.name || combat.attacker
  const defenderName = countries[combat.defender]?.name || combat.defender
  const isVictory = combat.outcome === 'victory' || combat.winner === combat.attacker
  const isDefeat = combat.outcome === 'defeat' || combat.winner === combat.defender
  const isStalemate = combat.outcome === 'stalemate' || (!isVictory && !isDefeat)

  const outcomeStyles = {
    victory: 'border-green-500/50 bg-green-500/10',
    defeat: 'border-red-500/50 bg-red-500/10',
    stalemate: 'border-yellow-500/50 bg-yellow-500/10'
  }

  const outcomeLabels = {
    victory: { text: 'Victory', icon: '🏆', color: 'text-green-400' },
    defeat: { text: 'Defeat', icon: '💀', color: 'text-red-400' },
    stalemate: { text: 'Stalemate', icon: '⚔️', color: 'text-yellow-400' }
  }

  const outcome = isVictory ? 'victory' : isDefeat ? 'defeat' : 'stalemate'
  const outcomeInfo = outcomeLabels[outcome]

  return (
    <div className={`rounded-lg border p-3 ${outcomeStyles[outcome]} transition-all hover:scale-[1.01]`}>
      {/* Battle Header */}
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2 text-sm">
          <span className="font-medium text-gray-200">{attackerName}</span>
          <span className="text-gray-500">→</span>
          <span className="font-medium text-gray-200">{defenderName}</span>
        </div>
        <div className={`flex items-center gap-1 ${outcomeInfo.color}`}>
          <span>{outcomeInfo.icon}</span>
          <span className="text-xs font-semibold uppercase">{outcomeInfo.text}</span>
        </div>
      </div>

      {/* Battle Details */}
      <div className="grid grid-cols-2 gap-2 text-xs">
        <div className="flex items-center gap-1.5">
          <span className="text-gray-500">Attacker losses:</span>
          <span className="text-red-400 font-mono">
            -{combat.attackerLosses ?? combat.attacker_losses ?? '?'}
          </span>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="text-gray-500">Defender losses:</span>
          <span className="text-red-400 font-mono">
            -{combat.defenderLosses ?? combat.defender_losses ?? '?'}
          </span>
        </div>
      </div>

      {/* Territory Change */}
      {isVictory && (
        <div className="mt-2 pt-2 border-t border-white/10 flex items-center gap-1.5 text-xs text-green-400">
          <span>🏴</span>
          <span>Territory captured</span>
        </div>
      )}
    </div>
  )
}

function EconomyChange({ country, previousEconomy, currentEconomy }) {
  const change = currentEconomy - previousEconomy
  const isPositive = change >= 0

  return (
    <div className="flex items-center justify-between py-1.5 border-b border-war-room-600/30 last:border-0">
      <span className="text-sm text-gray-300">{country.name}</span>
      <div className="flex items-center gap-2">
        <span className="text-xs text-gray-500">{previousEconomy}</span>
        <span className="text-gray-500">→</span>
        <span className={`text-sm font-mono ${isPositive ? 'text-green-400' : 'text-red-400'}`}>
          {currentEconomy}
          <span className="text-xs ml-1">
            ({isPositive ? '+' : ''}{change})
          </span>
        </span>
      </div>
    </div>
  )
}

function AIIntentSummary({ intents }) {
  if (!intents || intents.length === 0) return null

  return (
    <div className="space-y-2">
      {intents.map((intent, idx) => (
        <div key={idx} className="flex items-start gap-2 text-sm">
          <span className="text-gray-500">•</span>
          <span className="text-gray-300">{intent}</span>
        </div>
      ))}
    </div>
  )
}

function DiplomacyEventCard({ event, players }) {
  const getPlayerName = (id) => players[id]?.name || id

  const eventConfig = {
    diplomacy_proposal: { icon: '📜', label: 'Treaty Proposed', color: 'text-blue-400', bgColor: 'bg-blue-500/10', borderColor: 'border-blue-500/30' },
    diplomacy_accepted: { icon: '🤝', label: 'Treaty Accepted', color: 'text-green-400', bgColor: 'bg-green-500/10', borderColor: 'border-green-500/30' },
    diplomacy_rejected: { icon: '❌', label: 'Treaty Rejected', color: 'text-red-400', bgColor: 'bg-red-500/10', borderColor: 'border-red-500/30' },
    diplomacy_cancelled: { icon: '💔', label: 'Treaty Cancelled', color: 'text-orange-400', bgColor: 'bg-orange-500/10', borderColor: 'border-orange-500/30' },
    treaty_betrayed: { icon: '🗡️', label: 'Betrayal!', color: 'text-red-400', bgColor: 'bg-red-500/20', borderColor: 'border-red-500/50' },
    treaty_expired: { icon: '⏳', label: 'Treaty Expired', color: 'text-gray-400', bgColor: 'bg-gray-500/10', borderColor: 'border-gray-500/30' }
  }

  const config = eventConfig[event.type] || eventConfig.diplomacy_proposal

  return (
    <div className={`flex items-center gap-3 p-2 rounded-lg ${config.bgColor} border ${config.borderColor}`}>
      <span className="text-lg">{config.icon}</span>
      <div className="flex-1">
        <div className={`text-sm font-medium ${config.color}`}>{config.label}</div>
        <div className="text-xs text-gray-400">
          {event.from && event.to && `${getPlayerName(event.from)} → ${getPlayerName(event.to)}`}
          {event.players && event.players.map(getPlayerName).join(' & ')}
          {event.treaty && ` (${event.treaty.replace(/_/g, ' ')})`}
        </div>
      </div>
      {event.was_honored !== undefined && (
        <span className={`text-xs ${event.was_honored ? 'text-green-400' : 'text-red-400'}`}>
          {event.was_honored ? 'Honored' : 'Broken'}
        </span>
      )}
    </div>
  )
}

/**
 * Reputation change summary card
 */
function ReputationChangeCard({ reputationChange }) {
  if (!reputationChange) return null
  
  const { oldScore, newScore, delta, oldTier, newTier, reason } = reputationChange
  const isPositive = delta > 0
  const tierChanged = oldTier !== newTier
  
  const tierConfig = {
    REVERED: { icon: '👑', color: 'text-green-400', label: 'Revered' },
    RESPECTED: { icon: '⭐', color: 'text-blue-400', label: 'Respected' },
    NEUTRAL: { icon: '⚖️', color: 'text-gray-400', label: 'Neutral' },
    DISLIKED: { icon: '👎', color: 'text-orange-400', label: 'Disliked' },
    PARIAH: { icon: '💀', color: 'text-red-400', label: 'Pariah' }
  }
  
  const currentTierInfo = tierConfig[newTier] || tierConfig.NEUTRAL
  
  return (
    <div className={`rounded-lg border p-3 ${isPositive ? 'bg-green-500/10 border-green-500/30' : 'bg-red-500/10 border-red-500/30'}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-xl">{currentTierInfo.icon}</span>
          <div>
            <div className={`font-medium ${currentTierInfo.color}`}>
              Global Reputation: {currentTierInfo.label}
            </div>
            <div className="text-xs text-gray-400">{reason}</div>
          </div>
        </div>
        <div className="text-right">
          <div className={`text-lg font-bold ${isPositive ? 'text-green-400' : 'text-red-400'}`}>
            {isPositive ? '+' : ''}{delta}
          </div>
          <div className="text-xs text-gray-500">{oldScore} → {newScore}</div>
        </div>
      </div>
      {tierChanged && (
        <div className="mt-2 pt-2 border-t border-white/10 flex items-center gap-2 text-sm">
          <span className="text-gray-400">Tier changed:</span>
          <span className={tierConfig[oldTier]?.color || 'text-gray-400'}>{tierConfig[oldTier]?.label || oldTier}</span>
          <span className="text-gray-500">→</span>
          <span className={tierConfig[newTier]?.color || 'text-gray-400'}>{tierConfig[newTier]?.label || newTier}</span>
        </div>
      )}
    </div>
  )
}

/**
 * Trust change log entry
 */
function TrustChangeEntry({ log }) {
  const isPositive = log.delta > 0
  const stanceColors = {
    ALLY: 'text-green-400',
    FRIENDLY: 'text-green-300',
    NEUTRAL: 'text-gray-400',
    WARY: 'text-yellow-400',
    HOSTILE: 'text-red-400'
  }
  
  return (
    <div className="flex items-center justify-between py-1.5 border-b border-war-room-600/30 last:border-0">
      <div className="flex items-center gap-2">
        <span className={stanceColors[log.newStance] || 'text-gray-400'}>
          {log.countryName}
        </span>
        {log.stanceChanged && (
          <span className="text-xs px-1.5 py-0.5 rounded bg-war-room-700 text-gray-400">
            {log.oldStance} → {log.newStance}
          </span>
        )}
      </div>
      <div className="flex items-center gap-2">
        <span className="text-xs text-gray-500">{log.reason}</span>
        <span className={`font-mono text-sm ${isPositive ? 'text-green-400' : 'text-red-400'}`}>
          {isPositive ? '+' : ''}{log.delta}
        </span>
      </div>
    </div>
  )
}

/**
 * Diplomacy action outcome card
 */
function DiplomacyActionOutcomeCard({ outcome }) {
  const isAccepted = outcome.accepted
  
  return (
    <div className={`rounded-lg border p-3 ${isAccepted ? 'bg-green-500/10 border-green-500/30' : 'bg-red-500/10 border-red-500/30'}`}>
      <div className="flex items-center gap-3">
        <span className="text-xl">{outcome.icon}</span>
        <div className="flex-1">
          <div className={`font-medium ${isAccepted ? 'text-green-400' : 'text-red-400'}`}>
            {outcome.label} {isAccepted ? 'Accepted' : 'Rejected'}
          </div>
          <div className="text-xs text-gray-400">with {outcome.targetName}</div>
        </div>
        <div className="text-xs text-gray-500">
          {Math.round(outcome.acceptChance * 100)}% chance
        </div>
      </div>
    </div>
  )
}

function SectionHeader({ icon, title, count }) {
  return (
    <div className="flex items-center gap-2 mb-3 pb-2 border-b border-war-room-600/50">
      <span className="text-lg">{icon}</span>
      <h3 className="text-sm font-semibold text-gray-200 uppercase tracking-wide">{title}</h3>
      {count !== undefined && (
        <span className="ml-auto text-xs bg-war-room-700 px-2 py-0.5 rounded-full text-gray-400">
          {count}
        </span>
      )}
    </div>
  )
}

// ============================================================================
// TECH & EVENT COMPONENTS
// ============================================================================

/**
 * Tech completion notification card
 */
function TechCompletedCard({ tech }) {
  const branchColors = {
    military: { bg: 'bg-red-500/10', border: 'border-red-500/30', text: 'text-red-400', icon: '⚔️' },
    economy: { bg: 'bg-amber-500/10', border: 'border-amber-500/30', text: 'text-amber-400', icon: '💰' },
    governance: { bg: 'bg-blue-500/10', border: 'border-blue-500/30', text: 'text-blue-400', icon: '🏛️' }
  }
  
  const colors = branchColors[tech.branch] || branchColors.military
  
  return (
    <div className={`rounded-lg border p-3 ${colors.bg} ${colors.border}`}>
      <div className="flex items-center gap-2">
        <span className="text-xl">{colors.icon}</span>
        <div className="flex-1">
          <div className={`font-medium ${colors.text}`}>{tech.name}</div>
          <div className="text-xs text-gray-400">{tech.description}</div>
        </div>
        <span className="text-green-400 text-lg">✓</span>
      </div>
      {tech.effects && tech.effects.length > 0 && (
        <div className="mt-2 pt-2 border-t border-white/10 space-y-1">
          {tech.effects.slice(0, 3).map((effect, idx) => (
            <div key={idx} className="flex items-center gap-1.5 text-xs text-gray-300">
              <span className="text-green-400">+</span>
              <span>{effect.description}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

/**
 * Game event card with severity styling
 */
function GameEventCard({ event, onChoiceClick }) {
  const severityStyles = {
    minor: { bg: 'bg-green-500/10', border: 'border-green-500/30', text: 'text-green-400', badge: 'bg-green-500/20' },
    moderate: { bg: 'bg-amber-500/10', border: 'border-amber-500/30', text: 'text-amber-400', badge: 'bg-amber-500/20' },
    major: { bg: 'bg-orange-500/10', border: 'border-orange-500/30', text: 'text-orange-400', badge: 'bg-orange-500/20' },
    critical: { bg: 'bg-red-500/10', border: 'border-red-500/30', text: 'text-red-400', badge: 'bg-red-500/20' }
  }
  
  const categoryIcons = {
    economic: '💰',
    military: '⚔️',
    stability: '⚖️',
    natural: '🌍',
    diplomatic: '🤝',
    technology: '🔬'
  }
  
  const severity = event.severity || 'moderate'
  const styles = severityStyles[severity] || severityStyles.moderate
  const icon = categoryIcons[event.category] || '📋'
  
  const isChoice = event.requiresChoice || event.options
  const isPending = event.pendingChoice && !event.choiceMade
  
  return (
    <div className={`rounded-lg border p-3 ${styles.bg} ${styles.border} transition-all`}>
      <div className="flex items-start gap-2">
        <span className="text-xl">{icon}</span>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`font-medium ${styles.text}`}>{event.name}</span>
            <span className={`text-[10px] uppercase px-1.5 py-0.5 rounded ${styles.badge} ${styles.text}`}>
              {severity}
            </span>
            {event.scope && (
              <span className="text-[10px] text-gray-500 uppercase">
                {event.scope}
              </span>
            )}
          </div>
          <p className="text-xs text-gray-400 mt-1 line-clamp-2">{event.description}</p>
          
          {/* Effects applied */}
          {event.changes && event.changes.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1">
              {event.changes.slice(0, 4).map((change, idx) => (
                <span 
                  key={idx}
                  className={`text-[10px] px-1.5 py-0.5 rounded ${
                    change.change >= 0 ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
                  }`}
                >
                  {change.target}: {change.change >= 0 ? '+' : ''}{change.change}
                </span>
              ))}
            </div>
          )}
          
          {/* Choice indicator */}
          {isChoice && isPending && (
            <button
              onClick={() => onChoiceClick?.(event)}
              className="mt-2 text-xs px-2 py-1 bg-purple-500/20 border border-purple-500/50 rounded text-purple-400 hover:bg-purple-500/30 transition-colors"
            >
              ⚡ Choice Required
            </button>
          )}
          
          {/* Choice made */}
          {event.choiceMade && (
            <div className="mt-2 text-xs text-gray-400">
              <span className="text-green-400">✓</span> Chose: {event.choiceMade}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

/**
 * Crisis chain card with stage progression and escalation warnings
 */
function CrisisCard({ crisis }) {
  const originIcons = {
    economic: '💸',
    military: '⚔️',
    technology: '🔬',
    diplomatic: '🌐',
    environmental: '🌪️'
  }
  
  const stageStyles = [
    { bg: 'bg-yellow-500/10', border: 'border-yellow-500/30', text: 'text-yellow-400', badge: 'bg-yellow-500/20', label: 'Early Stage' },
    { bg: 'bg-orange-500/10', border: 'border-orange-500/30', text: 'text-orange-400', badge: 'bg-orange-500/20', label: 'Escalating' },
    { bg: 'bg-red-500/10', border: 'border-red-500/30', text: 'text-red-400', badge: 'bg-red-500/20', label: 'Critical' },
    { bg: 'bg-purple-500/10', border: 'border-purple-500/30', text: 'text-purple-400', badge: 'bg-purple-500/20', label: 'Catastrophic' }
  ]
  
  const stage = Math.min(crisis.stage || 0, stageStyles.length - 1)
  const styles = stageStyles[stage]
  const icon = originIcons[crisis.origin] || '⚠️'
  
  const isNew = crisis.type === 'crisis_started'
  const isEscalated = crisis.type === 'crisis_escalated'
  const isResolved = crisis.type === 'crisis_resolved'
  
  return (
    <div className={`rounded-lg border-2 p-3 ${styles.bg} ${styles.border} transition-all shadow-lg`}>
      <div className="flex items-start gap-2">
        <span className="text-2xl">{icon}</span>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <span className={`font-bold ${styles.text} text-base`}>
              {crisis.crisis_name || crisis.name}
            </span>
            <span className={`text-[10px] uppercase px-2 py-0.5 rounded font-bold ${styles.badge} ${styles.text}`}>
              {styles.label}
            </span>
            {isNew && (
              <span className="text-[10px] uppercase px-2 py-0.5 rounded bg-purple-500/20 text-purple-400 font-bold animate-pulse">
                NEW CRISIS
              </span>
            )}
            {isEscalated && (
              <span className="text-[10px] uppercase px-2 py-0.5 rounded bg-red-500/30 text-red-300 font-bold animate-pulse">
                ESCALATED
              </span>
            )}
            {isResolved && (
              <span className="text-[10px] uppercase px-2 py-0.5 rounded bg-green-500/20 text-green-400 font-bold">
                RESOLVED
              </span>
            )}
          </div>
          
          {/* Stage progression bar */}
          {!isResolved && (
            <div className="flex items-center gap-1 my-2">
              <span className="text-[10px] text-gray-500 uppercase">Stage:</span>
              <div className="flex gap-0.5 flex-1">
                {[0, 1, 2, 3].map(s => (
                  <div 
                    key={s}
                    className={`h-1.5 flex-1 rounded-full ${
                      s <= stage 
                        ? s === stage && isEscalated 
                          ? 'bg-red-400 animate-pulse' 
                          : styles.text.replace('text-', 'bg-')
                        : 'bg-war-room-600'
                    }`}
                  />
                ))}
              </div>
              <span className={`text-xs font-mono ${styles.text}`}>
                {stage + 1}/4
              </span>
            </div>
          )}
          
          {/* Affected territories */}
          {crisis.affected && crisis.affected.length > 0 && (
            <div className="flex items-center gap-1.5 mt-2 text-xs">
              <span className="text-gray-500">Affecting:</span>
              <div className="flex flex-wrap gap-1">
                {crisis.affected.slice(0, 3).map((countryId, idx) => (
                  <span key={idx} className="px-1.5 py-0.5 bg-war-room-700 rounded text-gray-300">
                    {countryId}
                  </span>
                ))}
                {crisis.affected.length > 3 && (
                  <span className="px-1.5 py-0.5 bg-war-room-700 rounded text-gray-500">
                    +{crisis.affected.length - 3} more
                  </span>
                )}
              </div>
            </div>
          )}
          
          {/* Resolution info */}
          {isResolved && crisis.resolution_type && (
            <div className="mt-2 pt-2 border-t border-white/10">
              <div className="flex items-center gap-2 text-xs text-green-400">
                <span>✓</span>
                <span>Resolution: {crisis.resolution_type.replace(/_/g, ' ')}</span>
              </div>
              {crisis.aftermath_duration && (
                <div className="text-xs text-gray-400 mt-1">
                  Aftermath effects for {crisis.aftermath_duration} turns
                </div>
              )}
            </div>
          )}
          
          {/* Warning for escalating crises */}
          {!isResolved && stage >= 2 && (
            <div className="mt-2 pt-2 border-t border-red-500/30 flex items-center gap-2 text-xs text-red-300">
              <span className="animate-pulse">⚠️</span>
              <span className="font-medium">
                {stage === 3 
                  ? 'FINAL STAGE - Immediate action required!'
                  : 'Crisis escalating - intervention needed'}
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

/**
 * Modifier breakdown tooltip content
 */
function ModifierBreakdown({ modifiers }) {
  if (!modifiers || modifiers.length === 0) return null
  
  return (
    <div className="space-y-1">
      {modifiers.map((mod, idx) => (
        <div key={idx} className="flex items-center justify-between gap-4 text-xs">
          <span className="text-gray-400 flex items-center gap-1">
            {mod.source === 'tech' && <span>🔬</span>}
            {mod.source === 'event' && <span>📋</span>}
            {mod.source === 'combat' && <span>⚔️</span>}
            {mod.source === 'building' && <span>🏗️</span>}
            <span>{mod.name}</span>
          </span>
          <span className={mod.value >= 0 ? 'text-green-400' : 'text-red-400'}>
            {mod.value >= 0 ? '+' : ''}{typeof mod.value === 'number' && mod.value < 1 && mod.value > -1 
              ? `${(mod.value * 100).toFixed(0)}%` 
              : mod.value}
          </span>
        </div>
      ))}
    </div>
  )
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export default function TurnSummaryModal({
  isOpen,
  onClose,
  turnNumber,
  combatResults = [],
  economyChanges = [],
  buildingsCompleted = [],
  aiIntents = [],
  territoriesGained = 0,
  territoriesLost = 0,
  majorEvents = [],
  diplomacyEvents = [],
  // Reputation and diplomacy action props
  reputationChange = null,
  trustChanges = [],
  diplomacyActionOutcomes = [],
  // New event system props
  techCompleted = [],
  gameEvents = [],
  choiceEvents = [],
  activeModifiers = {},
  onChoiceEvent,
  countries = {},
  players = {},
  // Crisis system props
  crisisEvents = [],
  activeCrises = []
}) {
  const modalRef = useRef(null)
  const closeButtonRef = useRef(null)

  // Handle ESC key
  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Escape') {
      onClose()
    }
  }, [onClose])

  // Focus trap and keyboard handling
  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown)
      // Focus the close button for accessibility
      setTimeout(() => closeButtonRef.current?.focus(), 100)
    }
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [isOpen, handleKeyDown])

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }
    return () => {
      document.body.style.overflow = ''
    }
  }, [isOpen])

  if (!isOpen) return null

  const hasCombat = combatResults.length > 0
  const hasEconomyChanges = economyChanges.length > 0
  const hasBuildingsCompleted = buildingsCompleted.length > 0
  const hasAIIntents = aiIntents.length > 0
  const hasMajorEvents = majorEvents.length > 0
  const hasDiplomacy = diplomacyEvents.length > 0 || reputationChange || trustChanges.length > 0 || diplomacyActionOutcomes.length > 0
  const hasTechCompleted = techCompleted.length > 0
  const hasGameEvents = gameEvents.length > 0 || choiceEvents.length > 0
  const allEvents = [...gameEvents, ...choiceEvents]
  const hasCrises = crisisEvents.length > 0 || activeCrises.length > 0
  const allCrisisEvents = [...crisisEvents]

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="turn-summary-title"
    >
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/60 backdrop-blur-sm animate-fade-in"
        onClick={onClose}
      />

      {/* Modal */}
      <div 
        ref={modalRef}
        className="relative w-full max-w-2xl max-h-[85vh] bg-war-room-800/95 backdrop-blur-md border border-war-room-600/50 rounded-2xl shadow-2xl animate-modal-in flex flex-col"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 pb-4 border-b border-war-room-600/50">
          <div>
            <h2 id="turn-summary-title" className="text-2xl font-bold text-white flex items-center gap-3">
              <span className="text-3xl">📋</span>
              Turn {turnNumber} Summary
            </h2>
            <p className="text-sm text-gray-400 mt-1">Here's what happened this turn</p>
          </div>
          <button
            ref={closeButtonRef}
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-war-room-700 transition-colors text-gray-400 hover:text-white focus:outline-none focus:ring-2 focus:ring-war-room-accent"
            aria-label="Close summary"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6 custom-scrollbar">
          {/* Global Overview */}
          <section>
            <SectionHeader icon="🌍" title="Global Overview" />
            <div className="grid grid-cols-3 gap-3">
              <div className="bg-war-room-700/50 rounded-lg p-3 text-center">
                <div className="text-2xl font-bold text-war-room-accent">{combatResults.length}</div>
                <div className="text-xs text-gray-400 uppercase">Battles</div>
              </div>
              <div className="bg-war-room-700/50 rounded-lg p-3 text-center">
                <div className="text-2xl font-bold text-green-400">+{territoriesGained}</div>
                <div className="text-xs text-gray-400 uppercase">Gained</div>
              </div>
              <div className="bg-war-room-700/50 rounded-lg p-3 text-center">
                <div className="text-2xl font-bold text-red-400">-{territoriesLost}</div>
                <div className="text-xs text-gray-400 uppercase">Lost</div>
              </div>
            </div>

            {/* Major Events */}
            {hasMajorEvents && (
              <div className="mt-3 space-y-1">
                {majorEvents.map((event, idx) => (
                  <div key={idx} className="flex items-center gap-2 text-sm py-1 px-2 bg-yellow-500/10 rounded text-yellow-400 border border-yellow-500/30">
                    <span>⚠️</span>
                    <span>{event}</span>
                  </div>
                ))}
              </div>
            )}
          </section>

          {/* Combat Results */}
          {hasCombat && (
            <section>
              <SectionHeader icon="⚔️" title="Combat Results" count={combatResults.length} />
              <div className="space-y-2">
                {combatResults.map((combat, idx) => (
                  <CombatCard key={idx} combat={combat} countries={countries} />
                ))}
              </div>
            </section>
          )}

          {/* Tech Completions */}
          {hasTechCompleted && (
            <section>
              <SectionHeader icon="🔬" title="Research Completed" count={techCompleted.length} />
              <div className="space-y-2">
                {techCompleted.map((tech, idx) => (
                  <TechCompletedCard key={idx} tech={tech} />
                ))}
              </div>
            </section>
          )}

          {/* Crisis Chains - High Priority Display */}
          {hasCrises && (
            <section>
              <SectionHeader 
                icon="⚠️" 
                title="Active Crises" 
                count={allCrisisEvents.length} 
              />
              <div className="space-y-3">
                {allCrisisEvents.map((crisis, idx) => (
                  <CrisisCard key={idx} crisis={crisis} />
                ))}
              </div>
              {activeCrises.length > 0 && allCrisisEvents.length === 0 && (
                <div className="text-xs text-gray-400 italic p-3 bg-war-room-700/30 rounded">
                  {activeCrises.length} {activeCrises.length === 1 ? 'crisis' : 'crises'} ongoing from previous turns
                </div>
              )}
            </section>
          )}

          {/* Game Events */}
          {hasGameEvents && (
            <section>
              <SectionHeader icon="📋" title="Events" count={allEvents.length} />
              <div className="space-y-2">
                {allEvents.map((event, idx) => (
                  <GameEventCard 
                    key={idx} 
                    event={event} 
                    onChoiceClick={onChoiceEvent}
                  />
                ))}
              </div>
            </section>
          )}

          {/* Diplomacy Events */}
          {hasDiplomacy && (
            <section>
              <SectionHeader 
                icon="🌐" 
                title="Diplomatic Activity" 
                count={diplomacyEvents.length + trustChanges.length + diplomacyActionOutcomes.length} 
              />
              <div className="space-y-3">
                {/* Reputation Change */}
                {reputationChange && (
                  <div className="mb-3">
                    <h4 className="text-xs text-gray-500 uppercase mb-2">Global Reputation</h4>
                    <ReputationChangeCard reputationChange={reputationChange} />
                  </div>
                )}
                
                {/* Diplomacy Action Outcomes */}
                {diplomacyActionOutcomes.length > 0 && (
                  <div className="mb-3">
                    <h4 className="text-xs text-gray-500 uppercase mb-2">Diplomatic Actions</h4>
                    <div className="space-y-2">
                      {diplomacyActionOutcomes.map((outcome, idx) => (
                        <DiplomacyActionOutcomeCard key={idx} outcome={outcome} />
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Treaty Events */}
                {diplomacyEvents.length > 0 && (
                  <div className="mb-3">
                    <h4 className="text-xs text-gray-500 uppercase mb-2">Treaty Changes</h4>
                    <div className="space-y-2">
                      {diplomacyEvents.map((event, idx) => (
                        <DiplomacyEventCard key={idx} event={event} players={players} />
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Trust Changes */}
                {trustChanges.length > 0 && (
                  <div>
                    <h4 className="text-xs text-gray-500 uppercase mb-2">Trust Changes</h4>
                    <div className="bg-war-room-700/30 rounded-lg p-3">
                      {trustChanges.map((log, idx) => (
                        <TrustChangeEntry key={idx} log={log} />
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </section>
          )}

          {/* Economy & Growth */}
          {(hasEconomyChanges || hasBuildingsCompleted) && (
            <section>
              <SectionHeader icon="💰" title="Economy & Growth" />
              
              {hasEconomyChanges && (
                <div className="mb-4">
                  <h4 className="text-xs text-gray-500 uppercase mb-2">Economic Changes</h4>
                  <div className="bg-war-room-700/30 rounded-lg p-3">
                    {economyChanges.map((change, idx) => (
                      <EconomyChange 
                        key={idx} 
                        country={change.country}
                        previousEconomy={change.previous}
                        currentEconomy={change.current}
                      />
                    ))}
                  </div>
                </div>
              )}

              {hasBuildingsCompleted && (
                <div>
                  <h4 className="text-xs text-gray-500 uppercase mb-2">Buildings Completed</h4>
                  <div className="flex flex-wrap gap-2">
                    {buildingsCompleted.map((building, idx) => (
                      <div key={idx} className="flex items-center gap-1.5 px-2 py-1 bg-blue-500/10 border border-blue-500/30 rounded text-sm text-blue-400">
                        <span>🏗️</span>
                        <span>{building.name}</span>
                        <span className="text-gray-500">in</span>
                        <span>{building.country}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </section>
          )}

          {/* AI Intent */}
          {hasAIIntents && (
            <section>
              <SectionHeader icon="🤖" title="AI Activity" count={aiIntents.length} />
              <AIIntentSummary intents={aiIntents} />
            </section>
          )}

          {/* Empty State */}
          {!hasCombat && !hasEconomyChanges && !hasMajorEvents && !hasDiplomacy && !hasTechCompleted && !hasGameEvents && (
            <div className="text-center py-8 text-gray-400">
              <span className="text-4xl mb-3 block">🕊️</span>
              <p>A peaceful turn with no major events.</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 pt-4 border-t border-war-room-600/50">
          <button
            onClick={onClose}
            className="w-full py-3 px-6 bg-war-room-accent hover:bg-blue-600 rounded-xl font-semibold text-white transition-all hover:scale-[1.02] active:scale-[0.98] focus:outline-none focus:ring-2 focus:ring-war-room-accent focus:ring-offset-2 focus:ring-offset-war-room-800"
          >
            Continue
          </button>
          <p className="text-center text-xs text-gray-500 mt-2">
            Press <kbd className="px-1.5 py-0.5 bg-war-room-700 rounded text-gray-400">ESC</kbd> or click outside to close
          </p>
        </div>
      </div>
    </div>
  )
}
